import { useCallback, useEffect, useRef, useState } from "react";
import { api, type NotificationItem, type NotificationsResponse } from "@/lib/api";
import { useAuth } from "./useAuth";

export type NotificationsState = {
	items: NotificationItem[];
	unreadCount: number;
	loading: boolean;
	error: string | null;
};

export function useNotifications() {
	const { getToken, isSignedIn } = useAuth();
	const [state, setState] = useState<NotificationsState>({
		items: [],
		unreadCount: 0,
		loading: false,
		error: null,
	});
	const getTokenRef = useRef(getToken);
	useEffect(() => {
		getTokenRef.current = getToken;
	}, [getToken]);

	const fetchNotifications = useCallback(async () => {
		if (!isSignedIn) return;
		setState((s) => ({ ...s, loading: true, error: null }));
		try {
			const token = await getTokenRef.current();
			if (!token) {
				setState((s) => ({ ...s, loading: false }));
				return;
			}
			const data: NotificationsResponse = await api.notifications(token);
			setState({
				items: data.notifications ?? [],
				unreadCount: data.unreadCount ?? 0,
				loading: false,
				error: null,
			});
		} catch (err) {
			setState((s) => ({
				...s,
				loading: false,
				error: err instanceof Error ? err.message : "Failed to load notifications",
			}));
		}
	}, [isSignedIn]);

	const fetchUnreadCount = useCallback(async () => {
		if (!isSignedIn) return;
		try {
			const token = await getTokenRef.current();
			if (!token) return;
			const data = await api.unreadCount(token);
			setState((s) => ({ ...s, unreadCount: data.unreadCount }));
		} catch {
			// ignore
		}
	}, [isSignedIn]);

	const markRead = useCallback(
		async (id: string) => {
			const token = await getTokenRef.current();
			if (!token) return;
			await api.markNotificationRead(token, id);
			setState((s) => ({
				...s,
				items: s.items.map((n) => (n.id === id ? { ...n, readAt: Date.now() } : n)),
				unreadCount: Math.max(0, s.unreadCount - (s.items.find((n) => n.id === id && !n.readAt) ? 1 : 0)),
			}));
		},
		[],
	);

	const markAllRead = useCallback(async () => {
		const token = await getTokenRef.current();
		if (!token) return;
		await api.markAllNotificationsRead(token);
		setState((s) => ({
			...s,
			items: s.items.map((n) => (n.readAt ? n : { ...n, readAt: Date.now() })),
			unreadCount: 0,
		}));
	}, []);

	useEffect(() => {
		if (isSignedIn) {
			fetchUnreadCount();
		} else {
			setState({ items: [], unreadCount: 0, loading: false, error: null });
		}
	}, [isSignedIn, fetchUnreadCount]);

	const pushNotification = useCallback((notif: NotificationItem) => {
		setState((s) => {
			if (s.items.some((n) => n.id === notif.id)) return s;
			return {
				...s,
				items: [notif, ...s.items],
				unreadCount: s.unreadCount + 1,
			};
		});
	}, []);

	return {
		...state,
		fetchNotifications,
		fetchUnreadCount,
		markRead,
		markAllRead,
		pushNotification,
	};
}
